import type { TFunction } from 'i18next';
import { escape } from 'lodash-es';
import { useSelector } from 'react-redux';

import type { RootState } from 'store';

import { AuthUserType, UserSubType } from 'common/graphql/types';
import { useMutation } from 'common/hooks/useMutation';
import { AuditCreationEventsEnum, AuditEntityIdEnum } from 'common/log/auditevents';
import { CIAuditLogger } from 'common/log/ci.auditlogger';
import { CILogger } from 'common/log/log.provider';
import { updateSecurityQuestionsGql } from 'module/patientApp/activate/activate.api';

export interface SecurityQuestionsFormValues {
    questionOne: string;
    questionOneAnswer: string;
    questionTwo: string;
    questionTwoAnswer: string;
    questionThree: string;
    questionThreeAnswer: string;
}

export function useSecurityQuestionsForm(t: TFunction) {
    const activeUser = useSelector((state: RootState) => state.auth.activeUser);
    const [updateSecurityQuestions, { isLoading }] = useUpdateSecurityQuestions();

    const submitQuestions = async (values: SecurityQuestionsFormValues): Promise<void> => {
        await updateSecurityQuestions(
            {
                userId: activeUser.pk,
                questions: {
                    securityQuestion1Id: '1',
                    securityQuestion1Text: values.questionOne,
                    securityQuestion1Answer: escape(values.questionOneAnswer),
                    securityQuestion2Id: '2',
                    securityQuestion2Text: values.questionTwo,
                    securityQuestion2Answer: escape(values.questionTwoAnswer),
                    securityQuestion3Id: '3',
                    securityQuestion3Text: values.questionThree,
                    securityQuestion3Answer: escape(values.questionThreeAnswer),
                },
            },

            {
                successMessage: t('security_questions.update_success'),
            }
        );
        const isPatient = activeUser.userType === AuthUserType.PATIENT;
        const isCaregiver = activeUser.subType === UserSubType.CAREGIVER;

        CIAuditLogger.getInstance().log(
            AuditCreationEventsEnum.Update,
            isPatient
                ? `${isCaregiver ? 'Caregiver' : 'Patient'} ${
                      activeUser.username
                  } successfully changed security questions on Patient Web`
                : `User ${activeUser.username} updated their Security Questions & Answers`,
            null,
            {
                relatedEntityName: AuditEntityIdEnum.User,
                relatedEntityId: activeUser.pk,
                subjectId: activeUser.pk,
            }
        );

        CILogger.getInstance().info(`${activeUser.pk} changed security questions & answers`);
    };

    return { submitQuestions, isLoading };
}

const useUpdateSecurityQuestions = () =>
    useMutation<object>(updateSecurityQuestionsGql, {
        queryString: 'updateUserSecurityQuestions',
        logErrorMessage: 'Failed to update security questions on Patient Web',
        throwOnError: true,
    });
